import { isClawBlacklisted, CLAW_CONFIG_KEY } from './clawAccessConfig'

export const CLAW_BETA_SHORT_IDS_CONFIG_KEY = 'todayclaw_beta_short_ids'
const DEFAULT_EXPIRE_AT = '2026-03-28'

/**
 * 可见范围（角色控制）— 与 clawAccessConfig 的 ClawVisibility 一致
 *
 * 角色层级（从高到低）：
 *   MASTER_ADMIN(1)      主管理员
 *   ADMIN(2)             管理员
 *   DEPARTMENT_ADMIN(3)  部门管理员
 *   NORMAL(0)            普通成员
 *
 * visibility 含义：
 * - master_admin: 仅主管理员可见
 * - admin:        主管理员 + 管理员可见
 * - dept_admin:   主管理员 + 管理员 + 部门管理员可见
 * - all:          团队所有成员可见（默认）
 */
type ClawBetaVisibility = 'master_admin' | 'admin' | 'dept_admin' | 'all'

const DEFAULT_VISIBILITY: ClawBetaVisibility = 'master_admin'

const buildShownStorageKey = (teamShortId: string): string =>
  `todayclaw_beta_approved_shown_${String(teamShortId || '').trim()}`

const normalizeShortIds = (list: unknown): string[] =>
  (Array.isArray(list) ? list : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean)

const toEndOfDayTime = (dateStr?: string | null): number => {
  if (!dateStr) return Number.NaN
  const normalized = String(dateStr).trim()
  const d = new Date(`${normalized}T23:59:59.999`)
  return d.getTime()
}

/**
 * 检查角色是否满足可见性要求
 * @param myRole - 当前用户角色 MASTER_ADMIN=1, ADMIN=2, DEPARTMENT_ADMIN=3, NORMAL=0
 * @param visibility - 可见性配置
 */
const isRoleVisible = (
  myRole: number,
  visibility: ClawBetaVisibility
): boolean => {
  // MASTER_ADMIN(1) 始终可见
  if (myRole === 1) return true
  if (visibility === 'master_admin') return false
  // ADMIN(2)
  if (myRole === 2) return true
  if (visibility === 'admin') return false
  // DEPARTMENT_ADMIN(3)
  if (myRole === 3) return true
  if (visibility === 'dept_admin') return false
  // visibility === 'all' → 所有成员可见
  return visibility === 'all'
}

export const parseClawBetaShortIds = (
  rawConfigValue: string | null | undefined
): string[] => {
  if (!rawConfigValue) return []
  try {
    const parsed = JSON.parse(rawConfigValue)
    if (Array.isArray(parsed)) return normalizeShortIds(parsed)
    if (Array.isArray(parsed.shortTeamIds))
      return normalizeShortIds(parsed.shortTeamIds)
    if (Array.isArray(parsed.teamShortIds))
      return normalizeShortIds(parsed.teamShortIds)
    if (Array.isArray(parsed.ids)) return normalizeShortIds(parsed.ids)
    if (parsed.enabled === false) return []
    if (typeof parsed.value === 'string') {
      return parsed.value
        .split(',')
        .map((item: string) => item.trim())
        .filter(Boolean)
    }
    return []
  } catch (e) {
    return String(rawConfigValue)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
}

const parseClawBetaExpireAt = (
  rawConfigValue: string | null | undefined
): string => {
  if (!rawConfigValue) return DEFAULT_EXPIRE_AT
  try {
    const parsed = JSON.parse(rawConfigValue)
    const expireAt =
      parsed?.expireAt ||
      parsed?.expiredAt ||
      parsed?.endDate ||
      parsed?.deadline ||
      ''
    return String(expireAt || DEFAULT_EXPIRE_AT).trim()
  } catch (e) {
    return DEFAULT_EXPIRE_AT
  }
}

/**
 * 从远程配置中解析 visibility
 */
const parseClawBetaVisibility = (
  rawConfigValue: string | null | undefined
): ClawBetaVisibility => {
  if (!rawConfigValue) return DEFAULT_VISIBILITY
  try {
    const parsed = JSON.parse(rawConfigValue)
    const v = parsed?.visibility
    if (
      v === 'master_admin' ||
      v === 'admin' ||
      v === 'dept_admin' ||
      v === 'all'
    ) {
      return v
    }
    return DEFAULT_VISIBILITY
  } catch (e) {
    return DEFAULT_VISIBILITY
  }
}

const isWithinClawBetaExpireTime = (
  rawConfigValue: string | null | undefined
): boolean => {
  const expireAt = parseClawBetaExpireAt(rawConfigValue)
  const endAt = toEndOfDayTime(expireAt)
  if (Number.isNaN(endAt)) return false
  return Date.now() <= endAt
}

export const markClawBetaApprovedShown = (teamShortId?: string): void => {
  const teamShortIdStr = String(teamShortId || '').trim()
  if (!teamShortIdStr) return
  localStorage.setItem(buildShownStorageKey(teamShortIdStr), '1')
}

export const hasShownClawBetaApproved = (teamShortId?: string): boolean => {
  const teamShortIdStr = String(teamShortId || '').trim()
  if (!teamShortIdStr) return true
  return localStorage.getItem(buildShownStorageKey(teamShortIdStr)) === '1'
}

/**
 * 判断是否需要展示内测通过弹窗
 *
 * @param user        - 当前用户
 * @param teamShortId - 团队短 ID
 * @param myRole      - 当前用户角色（MASTER_ADMIN=1, ADMIN=2, DEPARTMENT_ADMIN=3, NORMAL=0）
 * @param mainConfig  - Redux mainConfig
 * @param modalClosed - 弹窗是否已被关闭
 */
export const shouldShowClawBetaApprovedModal = ({
  user,
  groupId,
  teamShortId,
  myRole = 0,
  mainConfig,
  modalClosed
}: {
  user: unknown
  groupId?: string
  teamShortId?: string
  myRole?: number
  mainConfig?: Record<string, any> | null
  modalClosed: boolean
}): boolean => {
  if (!user || modalClosed) return false
  const teamShortIdStr = String(teamShortId || '').trim()
  if (!teamShortIdStr) return false
  // 黑名单检查：大B端客户不展示内测弹窗
  const rawEntryConfig = mainConfig?.[CLAW_CONFIG_KEY]
  if (
    isClawBlacklisted(String(groupId || ''), teamShortIdStr, rawEntryConfig)
  ) {
    return false
  }
  const rawBetaConfig = mainConfig?.[CLAW_BETA_SHORT_IDS_CONFIG_KEY]
  if (!isWithinClawBetaExpireTime(rawBetaConfig)) return false
  const targetShortIds = parseClawBetaShortIds(rawBetaConfig)
  if (!targetShortIds.includes(teamShortIdStr)) return false
  // 角色可见性检查
  const visibility = parseClawBetaVisibility(rawBetaConfig)
  if (!isRoleVisible(myRole, visibility)) return false
  return !hasShownClawBetaApproved(teamShortIdStr)
}

export type { ClawBetaVisibility }
